/**
 * Client-side autocomplete against the public Photon instance (komoot.io).
 * Hits Photon directly from the browser for fast address/place suggestions.
 */
import { getBrowserLocale } from '../lib/i18n';

const PHOTON_URL = 'https://photon.komoot.io/api/';
// Rough Europe bounding box: west, south, east, north.
const EUROPE_BBOX = '-25,34,45,72';

export type PlaceType =
  | 'house'
  | 'amenity'
  | 'neighbourhood'
  | 'suburb'
  | 'village'
  | 'city'
  | 'other';

export interface Suggestion {
  name: string;
  lat: number;
  lng: number;
  countryCode: string;
  placeType: PlaceType;
  displayName: string;
}

interface PhotonProperties {
  name?: string;
  country?: string;
  countrycode?: string;
  state?: string;
  city?: string;
  district?: string;
  street?: string;
  housenumber?: string;
  osm_key?: string;
  osm_value?: string;
}

interface PhotonFeature {
  geometry: { coordinates: [number, number] }; // [lon, lat]
  properties: PhotonProperties;
}

interface PhotonResponse {
  features: PhotonFeature[];
}

const CITY_VALUES = new Set(['city', 'town']);
const VILLAGE_VALUES = new Set(['village', 'hamlet', 'municipality']);
const SUBURB_VALUES = new Set(['suburb']);
const NEIGHBOURHOOD_VALUES = new Set(['neighbourhood', 'quarter', 'borough']);
const HOUSE_KEYS = new Set(['building', 'house']);
const POI_KEYS = new Set(['amenity', 'shop', 'tourism', 'leisure', 'office', 'craft', 'healthcare']);

export function mapPlaceType(osmKey?: string, osmValue?: string): PlaceType {
  if (osmKey === 'place') {
    if (osmValue && CITY_VALUES.has(osmValue)) return 'city';
    if (osmValue && VILLAGE_VALUES.has(osmValue)) return 'village';
    if (osmValue && SUBURB_VALUES.has(osmValue)) return 'suburb';
    if (osmValue && NEIGHBOURHOOD_VALUES.has(osmValue)) return 'neighbourhood';
  }
  if (osmKey && HOUSE_KEYS.has(osmKey)) return 'house';
  if (osmKey && POI_KEYS.has(osmKey)) return 'amenity';
  return 'other';
}

/** Search radius by place type. */
export function placeTypeToRadiusKm(placeType: PlaceType): number {
  switch (placeType) {
    case 'house':
    case 'amenity':
      return 1.5;
    case 'neighbourhood':
      return 2.0;
    case 'suburb':
      return 2.5;
    case 'village':
      return 4.0;
    case 'city':
      return 8.0;
    default:
      return 2.0;
  }
}

function buildDisplayName(p: PhotonProperties): string {
  const parts: string[] = [];
  const streetLine = [p.street, p.housenumber].filter(Boolean).join(' ');
  if (streetLine) parts.push(streetLine);
  if (p.city && p.city !== p.name) parts.push(p.city);
  else if (p.district && p.district !== p.name) parts.push(p.district);
  if (p.state) parts.push(p.state);
  if (p.country) parts.push(p.country);
  return parts.join(', ');
}

export async function photonAutocomplete(
  query: string,
  biasLatLng: { lat: number; lng: number } | null,
  signal?: AbortSignal
): Promise<Suggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const url = new URL(PHOTON_URL);
  url.searchParams.set('q', trimmed);
  url.searchParams.set('limit', '5');
  url.searchParams.set('lang', getBrowserLocale().slice(0, 2));
  url.searchParams.set('bbox', EUROPE_BBOX);
  if (biasLatLng) {
    url.searchParams.set('lat', String(biasLatLng.lat));
    url.searchParams.set('lon', String(biasLatLng.lng));
  }

  // eslint-disable-next-line no-console
  console.log('[Photon] Query:', trimmed, 'URL:', url.toString());

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) throw new Error(`Photon HTTP ${res.status}`);
  const data = (await res.json()) as PhotonResponse;

  // eslint-disable-next-line no-console
  console.log('[Photon] Raw response:', JSON.stringify(data.features?.slice(0, 2)));

  const suggestions: Suggestion[] = [];
  for (const feature of data.features ?? []) {
    const [lng, lat] = feature.geometry.coordinates;
    const p = feature.properties;
    if (!p.name || !p.countrycode) continue;
    suggestions.push({
      name: p.name,
      lat,
      lng,
      countryCode: p.countrycode.toUpperCase(),
      placeType: mapPlaceType(p.osm_key, p.osm_value),
      displayName: buildDisplayName(p) || p.name,
    });
  }

  // eslint-disable-next-line no-console
  console.log('[Photon] Suggestions:', suggestions.map(s => `${s.name} @ ${s.lat.toFixed(6)},${s.lng.toFixed(6)}`));

  return suggestions;
}
